import { useCallback, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import type { IFilterData } from '@rankmi/rankmi-table/dist/interfaces/filterProps'

import {
  ExternalCourseRansack,
  QueryCoursesArgs,
  EXTERNAL_COURSES,
  TDataExternalCourses,
} from 'graphqlAPI'
import { IAreaModel } from 'models/area.model'
import { ChildFilterTypes } from 'enum'
import { FilterGroup } from 'helpers/filterData.helper'
import { usePaginationVariables } from 'hooks/usePaginationVariables'
import { CoursesTab } from '../../components/CoursesTab'
import { ApplyFiltersByType } from '../../types'

interface ExternalCoursesProps {
  area?: IAreaModel | null
  filterData?: IFilterData

  applyFiltersByType: ApplyFiltersByType
}

export const ExternalCourses = ({
  area,
  applyFiltersByType,
}: ExternalCoursesProps) => {
  const { text, perPage, page, sorts, setPerPage, setPage, setSorts, setText } =
    usePaginationVariables()

  const areaId = area?.id?.toString()

  type Args = QueryCoursesArgs & {
    q2?: ExternalCourseRansack
  }

  const gExternal = useMemo(
    () => applyFiltersByType(ChildFilterTypes.ExternalCourse),
    [applyFiltersByType]
  )

  useEffect(() => {
    setPage(1)
  }, [setPage, gExternal])

  const useVariables = useCallback(
    (g?: Record<string, string | string[]>[] | FilterGroup[]) => ({
      areaId,
      perPage,
      page,
      q:
        sorts.length || text.length || g?.length
          ? {
              sorts: sorts,
              search_name_cont: text,
              g,
            }
          : undefined,
    }),
    [areaId, perPage, page, sorts, text]
  )

  const externalCourseVariables = useVariables(gExternal.filters)

  const { data: dataExternalCourses, loading: loadingExternalCourses } =
    useQuery<TDataExternalCourses, Args>(EXTERNAL_COURSES, {
      variables: externalCourseVariables,
      fetchPolicy: 'cache-and-network',
    })

  const loading = loadingExternalCourses

  const { externalCourses } = { ...dataExternalCourses }

  return (
    <CoursesTab
      type={ChildFilterTypes.ExternalCourse}
      areaId={areaId}
      nodes={externalCourses?.nodes || []}
      total={externalCourses?.nodesCount || 0}
      loading={loading}
      page={page}
      setPage={setPage}
      setText={setText}
      setSorts={setSorts}
      setPerPage={setPerPage}
    />
  )
}
